import {
    ChangeDetectorRef,
    Directive,
    effect,
    EnvironmentInjector,
    inject,
    Injector,
    input,
    untracked,
    ViewContainerRef
} from '@angular/core';
import {filter, tap} from 'rxjs/operators';
import {DynamicComponentFactoryService, DynamicComponentRef} from '../../../../../components/dynamic-component';
import {Widget} from '../../../../../components/layout/widget/widget';
import {WidgetDescription} from '../../../../../components/layout/widget/widget-description';

@Directive({
    selector: 'widget-body-renderer',
    standalone: false
})
export class WidgetBodyRendererDirective {

    public readonly widget = input.required<Widget>();

    private widgetImplComponentRef: DynamicComponentRef<any>;

    private readonly environmentInjector = inject(EnvironmentInjector);

    private readonly dynamicComponentFactoryService = inject(DynamicComponentFactoryService);
    private readonly viewContainerRef = inject(ViewContainerRef);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly injector = inject(Injector);

    private readonly _widgetChangeEffect = effect(onCleanup => {
        const widget = this.widget();

        if (widget) {
            const creationSub = widget.implementationInstance.ready$.pipe(
                filter(Boolean),
                tap(() => untracked(() => this.createComponent(widget)))
            ).subscribe();

            onCleanup(() => {
                creationSub.unsubscribe();
                this.destroyComponent();
            });
        }
    });

    private createComponent(widget: Widget): void {
        const widgetDescription: WidgetDescription = widget.widgetDescription;

        /**
         * This injector allows the widget's component to access its parent components and the widget instance.
         * @type {Injector}
         */
        const injector: Injector = Injector.create({
            name: `${widgetDescription.widgetTypeId} Component Injector`,
            parent: this.injector,
            providers: [{
                provide: Widget,
                useValue: widget
            }]
        });

        this.widgetImplComponentRef = this.dynamicComponentFactoryService.create(
            widgetDescription.widgetComponentType,
            {
                injector,
                environmentInjector: this.environmentInjector,
                place: {
                    viewContainerRef: this.viewContainerRef
                }
            }
        );

        /**
         * Required when it's used with {@link ChangeDetectionStrategy.OnPush}
         */
        this.changeDetectorRef.detectChanges();
    }

    private destroyComponent(): void {
        this.widgetImplComponentRef?.destroy();
        this.widgetImplComponentRef = null;
    }

}
